import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { tpAPI, categoryAPI, courseAPI } from "@/lib/api";
import { CourseGrouping } from "@/components/CourseGrouping";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  FlaskConical, 
  Pencil, 
  Trash2,
  FileText,
  ChevronRight,
  Loader2,
  Eye,
  EyeOff,
  Clock,
  BarChart3,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, cn } from "@/lib/utils";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function TPPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tps, setTps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewTP, setViewTP] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [courses, setCourses] = useState([]);

  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadTPs();
    categoryAPI.list(workspaceId).then(res => setCategories(res.data || [])).catch(() => {});
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadTPs = async () => {
    try {
      const response = await tpAPI.list(workspaceId);
      setTps(response.data);
    } catch (error) {
      console.error("Failed to load TPs:", error);
      toast.error("Erreur lors du chargement des TPs");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await tpAPI.delete(workspaceId, deleteId);
      setTps(tps.filter(t => t.id !== deleteId));
      toast.success("TP supprimé");
    } catch (error) {
      console.error("Failed to delete TP:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleViewTP = async (tpId) => {
    try {
      const response = await tpAPI.get(workspaceId, tpId);
      setViewTP(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const handleToggleVisibility = async (tpId) => {
    try {
      const response = await tpAPI.toggleVisibility(workspaceId, tpId);
      setTps(tps.map(t => 
        t.id === tpId ? { ...t, is_visible: response.data.is_visible } : t
      ));
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Erreur lors de la modification de la visibilité");
    }
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      easy: <Badge className="bg-green-500">Facile</Badge>,
      medium: <Badge className="bg-yellow-500">Moyen</Badge>,
      hard: <Badge className="bg-red-500">Difficile</Badge>,
    };
    return badges[difficulty] || <Badge>{difficulty}</Badge>;
  };

  // Category-based grouping
  const hasCategories = categories.length > 0;

  const tpsByGroup = tps.reduce((acc, tp) => {
    let groupKey;
    if (hasCategories) {
      groupKey = tp.category_id || "__uncategorized__";
    } else {
      groupKey = tp.chapter || "Sans chapitre";
    }
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(tp);
    return acc;
  }, {});

  // Sort TPs within each group by order
  Object.keys(tpsByGroup).forEach(key => {
    tpsByGroup[key].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  // Build sorted groups
  const sortedGroups = (() => {
    if (hasCategories) {
      const result = [];
      categories.forEach(cat => {
        if (tpsByGroup[cat.id]) {
          result.push({ key: cat.id, label: cat.name, color: cat.color, items: tpsByGroup[cat.id] });
        }
      });
      if (tpsByGroup["__uncategorized__"]) {
        result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, items: tpsByGroup["__uncategorized__"] });
      }
      return result;
    } else {
      return Object.entries(tpsByGroup)
        .sort((a, b) => {
          if (a[0] === "Sans chapitre") return 1;
          if (b[0] === "Sans chapitre") return -1;
          const minOrderA = Math.min(...a[1].map(t => t.order || 0));
          const minOrderB = Math.min(...b[1].map(t => t.order || 0));
          return minOrderA - minOrderB;
        })
        .map(([key, items]) => ({ key, label: key, color: null, items }));
    }
  })();

  // Auto-select first tab
  useEffect(() => {
    if (tps.length === 0) return;
    let currentValid = activeTab && sortedGroups.some(g => g.key === activeTab);
    if (!currentValid) {
      if (sortedGroups.length > 0) {
        setActiveTab(sortedGroups[0].key);
      } else {
        setActiveTab(null);
      }
    }
  }, [tps, categories, activeTab, sortedGroups]);

  // Group items within active tab by chapter (for category mode)
  const getChapterGroups = (items) => {
    const grouped = items.reduce((acc, tp) => {
      const chapter = tp.chapter || "Sans chapitre";
      if (!acc[chapter]) acc[chapter] = [];
      acc[chapter].push(tp);
      return acc;
    }, {});
    return Object.entries(grouped).sort((a, b) => {
      if (a[0] === "Sans chapitre") return 1;
      if (b[0] === "Sans chapitre") return -1;
      return 0;
    });
  };

  const renderTPCard = (tp, index, courseColor) => (
    <Card 
      key={tp.id} 
      className={cn(
        "card-hover transition-all relative overflow-hidden pl-2",
        tp.is_visible === false && "opacity-60 border-dashed"
      )}
      style={courseColor ? { borderColor: `${courseColor}33` } : {}}
      data-testid={`tp-card-${index}`}
    >
      {courseColor && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-1.5 transition-all" 
          style={{ backgroundColor: courseColor }}
        />
      )}
      <CardContent className="flex items-center gap-4 p-4">
        <div 
          className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 transition-colors"
          style={courseColor ? { backgroundColor: `${courseColor}15` } : {}}
          {...(!courseColor ? { className: "flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/20 shrink-0" } : {})}
        >
          <FlaskConical 
            className={cn("h-6 w-6 text-orange-600", courseColor && "text-foreground")} 
            style={courseColor ? { color: courseColor } : {}}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link 
              to={`/workspace/${workspaceId}/tp/${tp.id}/view`}
              className="font-semibold hover:text-primary transition-colors"
            >
              {tp.title}
            </Link>
            {canManage && tp.is_visible === false && (
              <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                Caché
              </Badge>
            )}
            {getDifficultyBadge(tp.difficulty)}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
            {tp.description || "Pas de description"}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {tp.duration_minutes} min
            </span>
            <span>Créé le {formatDate(tp.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleViewTP(tp.id)}
            data-testid={`view-tp-${index}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {canManage && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleVisibility(tp.id)}
                      data-testid={`toggle-visibility-tp-${index}`}
                    >
                      {tp.is_visible === false ? (
                        <EyeOff className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {tp.is_visible === false 
                      ? "Rendre visible aux candidats" 
                      : "Cacher aux candidats"
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/workspace/${workspaceId}/tp/${tp.id}`)}
                data-testid={`edit-tp-${index}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteId(tp.id)}
                data-testid={`delete-tp-${index}`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
          <Link to={`/workspace/${workspaceId}/tp/${tp.id}/view`}>
            <Button variant="ghost" size="icon">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Travaux Pratiques</h1>
            <p className="text-muted-foreground mt-1">
              {tps.length} TP{tps.length > 1 ? "s" : ""} dans cet espace
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <CategoryManagerButton />
              <Button className="gap-2" onClick={() => navigate(`/workspace/${workspaceId}/tp/new`)}>
                <Plus className="h-4 w-4" />
                Nouveau TP
              </Button>
            </div>
          )}
        </div>

        {/* Empty State */}
        {tps.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FlaskConical className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">Aucun TP</h3>
              <p className="text-muted-foreground mt-2 text-center max-w-md">
                {canManage
                  ? "Commencez par créer votre premier travail pratique."
                  : "Aucun TP n'est disponible pour le moment."}
              </p>
              {canManage && (
                <Button className="mt-4 gap-2" onClick={() => navigate(`/workspace/${workspaceId}/tp/new`)}>
                  <Plus className="h-4 w-4" />
                  Créer un TP
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div>
            {/* Category Tabs */}
            {sortedGroups.length > 1 || hasCategories ? (
              <>
                <div className="flex flex-wrap gap-3 mb-6 relative z-10 w-full">
                  {sortedGroups.map((group) => (
                    <div 
                      key={group.key}
                      onClick={() => setActiveTab(group.key)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all select-none min-w-max",
                        activeTab === group.key 
                          ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary/20" 
                          : "bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {group.color ? (
                        <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: group.color }} />
                      ) : (
                        <FileText className={cn("h-4 w-4", activeTab === group.key ? "text-primary" : "")} />
                      )}
                      <span className={cn("font-medium text-sm", activeTab === group.key ? "text-foreground" : "")}>
                        {group.label}
                      </span>
                      <span className="text-xs opacity-70 ml-1">{group.items.length}</span>
                    </div>
                  ))}
                </div>

                {/* Active Tab Content */}
                {sortedGroups.map((group) => {
                  if (group.key !== activeTab) return null;

                  // Show items grouped by course within each category tab
                  return (
                    <div key={group.key} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <CourseGrouping
                        items={group.items}
                        courses={courses}
                        renderItem={renderTPCard}
                        emptyLabel="Sans cours"
                        icon={FlaskConical}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              /* Fallback: no category tabs — group by course directly */
              <CourseGrouping
                items={tps}
                courses={courses}
                renderItem={renderTPCard}
                emptyLabel="Sans cours"
                icon={FlaskConical}
              />
            )}
          </div>
        )}

        {/* View TP Dialog */}
        <Dialog open={!!viewTP} onOpenChange={() => setViewTP(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewTP?.title}
                {viewTP && getDifficultyBadge(viewTP.difficulty)}
              </DialogTitle>
            </DialogHeader>
            {viewTP && (
              <div className="space-y-4">
                {viewTP.description && (
                  <p className="text-muted-foreground">{viewTP.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Durée: {viewTP.duration_minutes} minutes
                  </span>
                </div>
                <div 
                  className="prose prose-sm dark:prose-invert max-w-none border rounded-lg p-4"
                  dangerouslySetInnerHTML={{ __html: (() => {
                    try {
                      const raw = viewTP.content || "<p>Pas de contenu</p>";
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(raw, 'text/html');
                      doc.querySelectorAll('script, noscript').forEach(el => el.remove());
                      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
                      const toRemove = [];
                      while (walker.nextNode()) {
                        const t = walker.currentNode.textContent.trim();
                        if (t && (t.includes('navigator.clipboard') || t.includes('.cmd-box') || t.includes('.querySelectorAll') || t.includes('.querySelector(') || (t.includes('button.textContent') && t.includes('setTimeout')))) {
                          toRemove.push(walker.currentNode);
                        }
                      }
                      toRemove.forEach(n => n.parentNode.removeChild(n));
                      return doc.body.innerHTML;
                    } catch(e) {
                      return (viewTP.content || "<p>Pas de contenu</p>").replace(/<script\b[^>]*>[\s\S]*?<\/\s*script\s*>/gi, '');
                    }
                  })() }}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setViewTP(null)}>
                    Fermer
                  </Button>
                  <Link to={`/workspace/${workspaceId}/tp/${viewTP.id}/view`}>
                    <Button>
                      Ouvrir en plein écran
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce TP ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le TP sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </WorkspaceLayout>
  );
}
